import React, { createContext, useContext, useEffect, useState } from 'react';
import { cognitoAuthApi, LoginData, InviteUserData } from '../services/cognitoApi';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  groups?: string[];
}

interface CognitoAuthContextType {
  user: User | null;
  loading: boolean;
  login: (data: LoginData) => Promise<void>;
  logout: () => void;
  inviteUser: (data: InviteUserData) => Promise<void>;
  setPassword: (email: string, password: string) => Promise<void>;
  completePasswordChallenge: (data: { email: string; session: string; newPassword: string }) => Promise<void>;
  forgotPassword: (email: string) => Promise<any>;
  confirmPassword: (email: string, verificationCode: string, newPassword: string) => Promise<any>;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

const CognitoAuthContext = createContext<CognitoAuthContextType | undefined>(undefined);

export const useCognitoAuth = () => {
  const context = useContext(CognitoAuthContext);
  if (context === undefined) {
    throw new Error('useCognitoAuth must be used within a CognitoAuthProvider');
  }
  return context;
};

interface CognitoAuthProviderProps {
  children: React.ReactNode;
}

export const CognitoAuthProvider: React.FC<CognitoAuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Mock admin user for local development only (requires explicit flag)
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_MOCK_ADMIN === 'true') {
        console.log('Mock: Using mock admin user for development');
        const mockUser = {
          id: '88212330-e061-70f6-1871-ad9515ce2400',
          email: 'admin@test.com',
          first_name: 'Mock',
          last_name: 'Admin',
          groups: ['StudyHall-Admins']
        };

        localStorage.setItem('cognito_access_token', 'mock-admin-token');
        localStorage.setItem('user', JSON.stringify(mockUser));
        setUser(mockUser);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('cognito_access_token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);

          // Verify token is still valid by fetching current user
          const currentUser = await cognitoAuthApi.getCurrentUser();
          setUser(currentUser);
          localStorage.setItem('user', JSON.stringify(currentUser));
        } catch (error) {
          console.error('Token validation failed:', error);
          localStorage.removeItem('cognito_access_token');
          localStorage.removeItem('cognito_refresh_token');
          localStorage.removeItem('cognito_id_token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (data: LoginData) => {
    try {
      console.log('CognitoAuthContext: Starting API login for:', data.email);
      const response: any = await cognitoAuthApi.login(data);
      console.log('CognitoAuthContext: API login successful, response:', response);

      // Handle the actual response structure from the API
      const authData = response.data || response;
      console.log('CognitoAuthContext: Auth data:', authData);

      // Check if this is a challenge response
      if (authData.challengeName === 'NEW_PASSWORD_REQUIRED') {
        console.log('CognitoAuthContext: NEW_PASSWORD_REQUIRED challenge detected');
        const error: any = new Error('NEW_PASSWORD_REQUIRED');
        error.code = 'NEW_PASSWORD_REQUIRED';
        error.session = authData.session;
        error.email = authData.email;
        throw error;
      }

      // Store tokens
      localStorage.setItem('cognito_access_token', authData.token);
      localStorage.setItem('cognito_refresh_token', authData.refreshToken);
      localStorage.setItem('user', JSON.stringify(authData.user));

      console.log('CognitoAuthContext: Setting user state:', authData.user);
      setUser(authData.user);
      console.log('CognitoAuthContext: User state set, isAuthenticated will be:', !!authData.user);
    } catch (error) {
      console.error('CognitoAuthContext: Login failed:', error);
      throw error;
    }
  };

  const logout = () => {
    cognitoAuthApi.logout();
    setUser(null);
  };

  const inviteUser = async (data: InviteUserData) => {
    try {
      return await cognitoAuthApi.inviteUser(data);
    } catch (error) {
      throw error;
    }
  };

  const setPassword = async (email: string, password: string) => {
    try {
      return await cognitoAuthApi.setPassword(email, password);
    } catch (error) {
      throw error;
    }
  };

  const completePasswordChallenge = async (data: { email: string; session: string; newPassword: string }) => {
    try {
      const response = await cognitoAuthApi.completePasswordChallenge(data);
      // Handle the actual response structure from the API
      const authData = response.data || response;
      // Store authentication tokens and user info
      localStorage.setItem('cognito_access_token', authData.token);
      localStorage.setItem('cognito_refresh_token', authData.refreshToken);
      localStorage.setItem('user', JSON.stringify(authData.user));
      setUser(authData.user);
      return authData;
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      return await cognitoAuthApi.forgotPassword(email);
    } catch (error) {
      throw error;
    }
  };

  const confirmPassword = async (email: string, verificationCode: string, newPassword: string) => {
    try {
      return await cognitoAuthApi.confirmPassword(email, verificationCode, newPassword);
    } catch (error) {
      throw error;
    }
  };

  const isAdmin = user?.groups?.includes('StudyHall-Admins') || false;

  const value = {
    user,
    loading,
    login,
    logout,
    inviteUser,
    setPassword,
    completePasswordChallenge,
    forgotPassword,
    confirmPassword,
    isAuthenticated: !!user,
    isAdmin,
  };

  return <CognitoAuthContext.Provider value={value}>{children}</CognitoAuthContext.Provider>;
};